import { generateSkillPath } from './skillPathAI';
import { TermsPage, PrivacyPage } from './LegalPages';
import { FAQPage } from './FAQPage';
import { CommunityPage } from './CommunityPage';
import Pricing from './Pricing';
import { useState, useEffect } from "react";
import { sendEmail } from './emailService';
import { VideoPlayer } from './VideoPlayer';
import { supabase } from './supabase';
import { useSupabaseUser } from './useSupabaseUser';
import { UploadCourseForm } from './UploadCourseForm';
import { AdminPage } from './AdminPage';
import { MyLearningPage } from './MyLearningPage';
import {
  SignedIn, SignedOut, SignInButton, UserButton, useUser, useClerk,
} from "@clerk/clerk-react";

const C = {
  bg:          '#020617',
  surface:     'rgba(255,255,255,0.05)',
  surface2:    'rgba(255,255,255,0.08)',
  border:      'rgba(255,255,255,0.10)',
  borderHov:   'rgba(255,255,255,0.20)',
  accent:      '#60A5FA',
  accentDim:   '#3B82F6',
  accentGlow:  'rgba(96,165,250,0.15)',
  accentGlow2: 'rgba(96,165,250,0.08)',
  violet:      '#A78BFA',
  violetGlow:  'rgba(167,139,250,0.15)',
  gold:        '#F0A500',
  goldGlow:    'rgba(240,165,0,0.12)',
  green:       '#34D399',
  greenGlow:   'rgba(52,211,153,0.12)',
  red:         '#F87171',
  text:        '#FFFFFF',
  textMuted:   '#94A3B8',
  textFaint:   '#475569',
  white:       '#FFFFFF',
};

const F = {
  display: "'Playfair Display', Georgia, serif",
  body:    "'DM Sans', 'Segoe UI', sans-serif",
};

// ── LOGO SVG ──────────────────────────────────────────────────
const LogoIcon = ({ size = 32 }) => (
  <svg width={size} height={size} viewBox="0 0 100 100" fill="none">
    <path d="M18 0 L66 0 Q100 0 100 34 L100 82 Q100 100 82 100 L18 100 Q0 100 0 82 L0 18 Q0 0 18 0 Z" fill="#1B3CC8"/>
    <path d="M66 0 Q100 0 100 34 L80 34 Q66 34 66 20 Z" fill="#F0C040"/>
    <rect x="22" y="36" width="38" height="7" rx="3.5" fill="white" opacity="0.95"/>
    <rect x="22" y="49" width="30" height="7" rx="3.5" fill="white" opacity="0.7"/>
    <rect x="22" y="62" width="22" height="7" rx="3.5" fill="white" opacity="0.45"/>
    <circle cx="70" cy="66" r="12" fill="#F0C040"/>
    <path d="M66 66 L70 70 L76 62" stroke="#1B3CC8" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
  </svg>
);

const LogoWordmark = ({ size = 'sm', dark = false }) => {
  const iconSize = size === 'lg' ? 44 : 32;
  const fontSize = size === 'lg' ? 28 : 20;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <LogoIcon size={iconSize} />
      <span style={{
        fontSize,
        fontWeight: 900,
        letterSpacing: '-0.5px',
        fontFamily: "'Helvetica Neue', Arial, sans-serif",
        color: dark ? '#FFFFFF' : '#FFFFFF',
        userSelect: 'none',
      }}>
        Skill<span style={{ color: '#60A5FA' }}>Bite</span>
      </span>
    </div>
  );
};

export const courses = [
  { id: 1, title: "Master ChatGPT Agents in 1 Hour", category: "AI Workflows", level: "Beginner", duration: "58 min", modules: 8, price: 0, rating: 4.9, students: 12400, instructor: "Alex Rivera", region: "Global", hot: true, new: false, thumbnail: "🤖", color: C.accent },
  { id: 2, title: "Create Viral TikTok Videos With AI", category: "Short Video", level: "Beginner", duration: "45 min", modules: 6, price: 29, rating: 4.8, students: 9800, instructor: "Maya Chen", region: "Global", hot: true, new: true, thumbnail: "🎬", color: C.red },
  { id: 3, title: "Start a WhatsApp Automation Agency", category: "No-Code", level: "Intermediate", duration: "2.1 hrs", modules: 12, price: 49, rating: 4.7, students: 7200, instructor: "Tunde Okafor", region: "Nigeria", hot: false, new: true, thumbnail: "📱", color: C.green },
  { id: 4, title: "Earn $5K/Month Selling AI Templates", category: "Solopreneur", level: "Intermediate", duration: "1.5 hrs", modules: 10, price: 39, rating: 4.8, students: 11000, instructor: "Sarah Kim", region: "Global", hot: true, new: false, thumbnail: "💰", color: C.gold },
  { id: 5, title: "AI Tools for Students: Study in Half the Time", category: "Productivity", level: "Beginner", duration: "42 min", modules: 7, price: 0, rating: 4.6, students: 18500, instructor: "Priya Sharma", region: "India", hot: false, new: false, thumbnail: "📚", color: "#00BCD4" },
  { id: 6, title: "No-Code App Creation with AI", category: "No-Code", level: "Beginner", duration: "1.2 hrs", modules: 9, price: 34, rating: 4.7, students: 6300, instructor: "Marcus Lee", region: "US", hot: false, new: true, thumbnail: "⚡", color: C.accentDim },
  { id: 7, title: "AI Graphic Design & Branding Masterclass", category: "Design", level: "Intermediate", duration: "1.8 hrs", modules: 11, price: 44, rating: 4.9, students: 5900, instructor: "Ana Costa", region: "Global", hot: true, new: false, thumbnail: "🎨", color: "#9C6EFF" },
  { id: 8, title: "Personal AI Agent: Build Your Digital Staff", category: "AI Workflows", level: "Advanced", duration: "2.4 hrs", modules: 14, price: 69, rating: 4.8, students: 4200, instructor: "James Wu", region: "US", hot: false, new: true, thumbnail: "🧠", color: C.accent },
  { id: 9, title: "WhatsApp Business Automation for Logistics", category: "No-Code", level: "Intermediate", duration: "1.3 hrs", modules: 8, price: 29, rating: 4.7, students: 3100, instructor: "Emeka Dike", region: "Nigeria", hot: false, new: false, thumbnail: "🚚", color: C.green },
  { id: 10, title: "Government Job Prep with AI Assistants", category: "Productivity", level: "Beginner", duration: "55 min", modules: 7, price: 19, rating: 4.5, students: 8700, instructor: "Rahul Gupta", region: "India", hot: false, new: false, thumbnail: "🏛️", color: C.gold },
  { id: 11, title: "AI-Powered Customer Service Agency", category: "Solopreneur", level: "Intermediate", duration: "2.0 hrs", modules: 13, price: 59, rating: 4.8, students: 3800, instructor: "Lisa Park", region: "Global", hot: true, new: false, thumbnail: "🎧", color: C.red },
  { id: 12, title: "Money & Productivity Micro-Habits with AI", category: "Productivity", level: "Beginner", duration: "38 min", modules: 5, price: 0, rating: 4.6, students: 22000, instructor: "David Osei", region: "Global", hot: false, new: false, thumbnail: "💡", color: "#CDDC39" },
];

const careerPacks = [
  { id: 1, title: "Become an AI Video Creator", icon: "🎬", duration: "30 days", courses: 5, price: 79, students: 3200, color: C.red, desc: "Master AI video tools, scripts, and viral strategies" },
  { id: 2, title: "Start a WhatsApp Chatbot Business", icon: "💬", duration: "30 days", courses: 6, price: 89, students: 2800, color: C.green, desc: "Build and sell WhatsApp automation to local businesses" },
  { id: 3, title: "Become a Micro-Influencer", icon: "⭐", duration: "30 days", courses: 4, price: 69, students: 4100, color: "#9C6EFF", desc: "Grow to 10K followers using AI content systems" },
  { id: 4, title: "Home Automation Agency", icon: "🏠", duration: "30 days", courses: 7, price: 99, students: 1900, color: C.gold, desc: "Sell smart home AI setups to homeowners" },
  { id: 5, title: "AI Solopreneur in a Box", icon: "🚀", duration: "30 days", courses: 8, price: 119, students: 2400, color: C.accent, desc: "Full stack: product, marketing, and revenue with AI" },
  { id: 6, title: "No-Code App Founder", icon: "⚡", duration: "30 days", courses: 5, price: 89, students: 2100, color: C.accentDim, desc: "Build and launch your first app without coding" },
];

const categories = ["All", "AI Workflows", "No-Code", "Short Video", "Solopreneur", "Design", "Productivity"];
const regions = ["Global", "Nigeria", "India", "US"];
const skillQuestions = [
  { q: "What's your primary goal?", opts: ["Get a better job", "Start a side hustle", "Grow my business", "Learn for fun"] },
  { q: "How much time can you dedicate daily?", opts: ["10–15 min", "30 min", "1 hour", "2+ hours"] },
  { q: "What's your current skill level?", opts: ["Complete beginner", "Some experience", "Intermediate", "Advanced"] },
  { q: "Which area excites you most?", opts: ["AI & Automation", "Content & Video", "Business & Money", "Design & Branding"] },
];

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;800;900&family=DM+Sans:wght@300;400;500;600;700&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html { scroll-behavior: smooth; }
  body { background: #020617; color: #FFFFFF; font-family: ${F.body}; -webkit-font-smoothing: antialiased; }
  ::-webkit-scrollbar { width: 5px; }
  ::-webkit-scrollbar-track { background: #020617; }
  ::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.15); border-radius: 3px; }
  ::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.25); }
  @keyframes fadeUp { from { opacity:0; transform:translateY(20px); } to { opacity:1; transform:translateY(0); } }
  @keyframes fadeIn { from { opacity:0; } to { opacity:1; } }
  @keyframes pulse { 0%,100% { opacity:1; } 50% { opacity:0.4; } }
  @keyframes float { 0%,100% { transform:translateY(0); } 50% { transform:translateY(-8px); } }
  @keyframes spin { from { transform:rotate(0deg); } to { transform:rotate(360deg); } }
  @keyframes shimmerMove { 0% { transform:translateX(-100%); } 100% { transform:translateX(200%); } }
  @keyframes streak { 0%,100% { box-shadow:0 0 0 0 rgba(96,165,250,0); } 50% { box-shadow:0 0 0 6px rgba(96,165,250,0.15); } }
  .fade-in { animation: fadeIn 0.4s ease forwards; }
  .fade-up { animation: fadeUp 0.5s ease forwards; }
  .nav-link { color:#94A3B8; font-family:${F.body}; font-size:14px; font-weight:500; padding:6px 12px; border-radius:8px; cursor:pointer; transition:all 0.2s; border:none; background:none; }
  .nav-link:hover { color:#FFFFFF; background:rgba(255,255,255,0.06); }
  .nav-link.active { color:#60A5FA; background:rgba(96,165,250,0.10); }
  .btn-primary { background:#FFFFFF; color:#020617; border:none; padding:12px 28px; border-radius:10px; font-family:${F.body}; font-weight:700; font-size:14px; cursor:pointer; transition:all 0.2s; letter-spacing:0.2px; }
  .btn-primary:hover { background:#F1F5F9; transform:translateY(-1px); box-shadow:0 8px 24px rgba(255,255,255,0.15); }
  .btn-secondary { background:transparent; color:#FFFFFF; border:1.5px solid rgba(255,255,255,0.20); padding:11px 28px; border-radius:10px; font-family:${F.body}; font-weight:600; font-size:14px; cursor:pointer; transition:all 0.2s; }
  .btn-secondary:hover { border-color:rgba(255,255,255,0.45); background:rgba(255,255,255,0.05); }
  .btn-ghost { background:transparent; color:#94A3B8; border:none; padding:8px 14px; border-radius:8px; font-family:${F.body}; font-weight:500; font-size:13px; cursor:pointer; transition:all 0.2s; }
  .btn-ghost:hover { color:#FFFFFF; background:rgba(255,255,255,0.06); }
  .card { background:rgba(255,255,255,0.05); border:1px solid rgba(255,255,255,0.10); border-radius:16px; transition:all 0.25s; overflow:hidden; }
  .card:hover { border-color:#60A5FA; transform:translateY(-3px); box-shadow:0 8px 32px rgba(96,165,250,0.15); }
  .tag { display:inline-flex; align-items:center; padding:3px 9px; border-radius:6px; font-size:11px; font-weight:700; letter-spacing:0.4px; text-transform:uppercase; }
  .progress-bar { height:4px; background:rgba(255,255,255,0.08); border-radius:2px; overflow:hidden; }
  .progress-fill { height:100%; background:linear-gradient(90deg,#60A5FA,#A78BFA); border-radius:2px; transition:width 1s ease; }
  .input-field { background:rgba(255,255,255,0.06); border:1.5px solid rgba(255,255,255,0.12); color:#FFFFFF; padding:10px 16px; border-radius:10px; font-family:${F.body}; font-size:14px; outline:none; transition:border-color 0.2s,box-shadow 0.2s; width:100%; }
  .input-field:focus { border-color:#60A5FA; box-shadow:0 0 0 3px rgba(96,165,250,0.15); }
  .input-field::placeholder { color:#475569; }
  select.input-field { appearance:none; cursor:pointer; }
  select.input-field option { background:#0F172A; color:#FFFFFF; }
  .section-title { font-family:${F.display}; font-size:clamp(26px,3.5vw,38px); font-weight:800; line-height:1.15; color:#FFFFFF; }
  .grid-courses { display:grid; grid-template-columns:repeat(auto-fill,minmax(280px,1fr)); gap:20px; }
  .pill-selector { display:flex; gap:8px; flex-wrap:wrap; }
  .pill { padding:7px 16px; border-radius:50px; font-size:13px; font-weight:500; cursor:pointer; transition:all 0.2s; border:1.5px solid rgba(255,255,255,0.12); background:transparent; color:#94A3B8; font-family:${F.body}; }
  .pill:hover { border-color:#60A5FA; color:#60A5FA; background:rgba(96,165,250,0.08); }
  .pill.active { background:#60A5FA; color:#020617; border-color:#60A5FA; font-weight:700; }
  .sidebar-item { display:flex; align-items:center; gap:10px; padding:10px 14px; border-radius:10px; cursor:pointer; transition:all 0.2s; font-size:14px; font-weight:500; color:#94A3B8; border:none; background:none; width:100%; text-align:left; font-family:${F.body}; }
  .sidebar-item:hover { background:rgba(255,255,255,0.06); color:#FFFFFF; }
  .sidebar-item.active { background:rgba(96,165,250,0.10); color:#60A5FA; }
  .modal-overlay { position:fixed; inset:0; background:rgba(2,6,23,0.85); backdrop-filter:blur(12px); z-index:100; display:flex; align-items:center; justify-content:center; animation:fadeIn 0.2s ease; }
  .modal-box { background:#0F172A; border:1px solid rgba(255,255,255,0.10); border-radius:20px; padding:36px; max-width:540px; width:90%; max-height:90vh; overflow-y:auto; animation:fadeUp 0.3s ease; box-shadow:0 24px 80px rgba(0,0,0,0.6); }
  .video-player { background:#000; border-radius:12px; aspect-ratio:16/9; display:flex; align-items:center; justify-content:center; position:relative; overflow:hidden; }
  .tab-bar { display:flex; border-bottom:1px solid rgba(255,255,255,0.10); gap:0; margin-bottom:24px; }
  .tab { padding:12px 20px; font-size:14px; font-weight:500; color:#94A3B8; cursor:pointer; border-bottom:2px solid transparent; transition:all 0.2s; font-family:${F.body}; background:none; border-top:none; border-left:none; border-right:none; margin-bottom:-1px; }
  .tab:hover { color:#FFFFFF; }
  .tab.active { color:#60A5FA; border-bottom-color:#60A5FA; }
  .stat-card { background:rgba(255,255,255,0.05); border:1px solid rgba(255,255,255,0.10); border-radius:16px; padding:20px; transition:all 0.25s; }
  .stat-card:hover { border-color:#60A5FA; box-shadow:0 4px 20px rgba(96,165,250,0.12); }
  .xp-bar { height:6px; border-radius:3px; background:rgba(255,255,255,0.08); overflow:hidden; margin-top:6px; }
  .xp-fill { height:100%; border-radius:3px; background:linear-gradient(90deg,#60A5FA,#34D399); transition:width 1s ease; }
  @media (max-width:900px) {
    .nav-link { font-size:11px !important; padding:5px 7px !important; }
  }
  @media (max-width:820px) {
    .nav-hide-md { display:none !important; }
  }
  @media (max-width:768px) {
    .nav-links-desktop { display:none !important; }
    .mobile-menu-btn { display:flex !important; }
    .mobile-menu { display:flex !important; }
    .hero-buttons { flex-direction:column !important; align-items:stretch !important; }
    .hero-buttons button { width:100% !important; }
    .hero-stats { gap:20px !important; flex-wrap:wrap !important; justify-content:center !important; }
    .grid-courses { grid-template-columns:1fr !important; }
    .grid-packs { grid-template-columns:1fr !important; }
    .grid-region { grid-template-columns:1fr 1fr !important; }
    .marketplace-layout { grid-template-columns:1fr !important; }
    .marketplace-sidebar { display:none !important; }
    .creator-stats { grid-template-columns:1fr 1fr !important; }
    .creator-earnings { grid-template-columns:1fr !important; }
    .modal-box { width:100% !important; max-width:100% !important; min-height:60vh !important; margin:0 !important; border-radius:20px 20px 0 0 !important; position:fixed !important; bottom:0 !important; left:0 !important; right:0 !important; }
    .modal-overlay { align-items:flex-end !important; }
    .pricing-plans { grid-template-columns:1fr !important; }
    .footer-inner { flex-direction:column !important; align-items:center !important; text-align:center !important; gap:16px !important; }
    .page-container { padding:20px 16px 60px !important; }
    .section-title { font-size:26px !important; }
  }
  @media (max-width:480px) {
    .grid-region { grid-template-columns:1fr !important; }
    .creator-stats { grid-template-columns:1fr !important; }
  }
`;

function Badge({ children, color = C.accent }) {
  return <span className="tag" style={{ background: color + "20", color, border: `1px solid ${color}40` }}>{children}</span>;
}

function StarRating({ rating }) {
  return (
    <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
      <span style={{ color: C.gold, fontSize: 12 }}>{"★".repeat(Math.floor(rating))}</span>
      <span style={{ color: C.textMuted, fontSize: 12 }}>{rating}</span>
    </span>
  );
}


function CourseCard({ course, onClick }) {
  return (
    <div className="card" style={{ cursor: "pointer" }} onClick={() => onClick(course)}>
      <div style={{ background: `linear-gradient(135deg, ${course.color}18, ${C.surface})`, padding: "24px 20px 18px", borderBottom: `1px solid ${C.border}`, position: "relative", minHeight: 100 }}>
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg, ${course.color}, transparent)`, borderRadius: "16px 16px 0 0" }} />
        <div style={{ fontSize: 40, marginBottom: 10 }}>{course.thumbnail}</div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {course.hot && <Badge color={C.gold}>🔥 Hot</Badge>}
          {course.new && <Badge color={C.accent}>✨ New</Badge>}
          {course.price === 0 && <Badge color={C.green}>Free</Badge>}
          <Badge color={course.color}>{course.category}</Badge>
        </div>
        {course.region !== "Global" && (
          <div style={{ position: "absolute", top: 14, right: 14, fontSize: 18 }}>
            {course.region === "Nigeria" ? "🇳🇬" : course.region === "India" ? "🇮🇳" : "🇺🇸"}
          </div>
        )}
      </div>
      <div style={{ padding: "16px 20px 20px" }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, lineHeight: 1.4, marginBottom: 8, color: C.text }}>{course.title}</h3>
        <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 10 }}>by {course.instructor}</div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
          <StarRating rating={course.rating} />
          <span style={{ color: C.textFaint, fontSize: 11 }}>·</span>
          <span style={{ fontSize: 11, color: C.textMuted }}>{course.students.toLocaleString()} students</span>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ display: "flex", gap: 10 }}>
            <span style={{ fontSize: 11, color: C.textMuted }}>⏱ {course.duration}</span>
            <span style={{ fontSize: 11, color: C.textMuted }}>📦 {course.modules}</span>
          </div>
          <div style={{ fontSize: 15, fontWeight: 800, color: course.price === 0 ? C.green : C.gold }}>
            {course.price === 0 ? "FREE" : `$${course.price}`}
          </div>
        </div>
      </div>
    </div>
  );
}

function CourseModal({ course, onClose }) {
  const [enrolled, setEnrolled] = useState(false);
  const { user } = useUser();
  const [playing, setPlaying] = useState(false);
  const [modules, setModules] = useState(() =>
    Array.from({ length: course.modules }, (_, i) => ({
      id: i + 1,
      title: `${i + 1}. ${["Introduction & Overview","Core Concepts","Hands-On Practice","Real-World Application","Advanced Techniques","Tools & Resources","Case Studies","Building Your System","Optimization","Scaling Up","Community","Final Project","Next Steps","Certification Prep"][i] || `Topic ${i + 1}`}`,
      duration: `${Math.floor(Math.random() * 8 + 4)} min`, done: i < 2,
    }))
  );
  const [realCourse, setRealCourse] = useState(null);

  useEffect(() => {
    if (!course?.id) return;
    supabase.from("courses")
      .select("mux_playback_id, title")
      .eq("id", course.id)
      .single()
      .then(({ data }) => { if (data?.mux_playback_id) setRealCourse(data); });
  }, [course?.id]);

  const updateProgress = async (moduleId) => {
    if (!user?.id || !course?.id) return;
    await supabase.from("progress").insert({
      user_id: user.id,
      course_id: course.id,
      module_id: moduleId,
      completed: true,
      completed_at: new Date().toISOString(),
    }).catch(() => {});

    const updatedModules = modules.map(m => m.id === moduleId ? { ...m, done: true } : m);
    setModules(updatedModules);

    const completedCount = updatedModules.filter(m => m.done).length;
    const progressPct = Math.round((completedCount / updatedModules.length) * 100);
    await supabase.from("enrollments")
      .update({ progress: progressPct, last_accessed: new Date().toISOString() })
      .eq("user_id", user.id)
      .eq("course_id", course.id);
  };
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={e => e.stopPropagation()} style={{ maxWidth: 680 }}>
        <div style={{ height: 4, background: `linear-gradient(90deg,${course.color},${C.accent})`, borderRadius: "20px 20px 0 0", margin: "-36px -36px 24px" }} />
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <div style={{ fontSize: 40 }}>{course.thumbnail}</div>
            <div>
              <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
                <Badge color={course.color}>{course.category}</Badge>
                <Badge color={course.price === 0 ? C.green : C.gold}>{course.price === 0 ? "Free" : `$${course.price}`}</Badge>
              </div>
              <div style={{ fontSize: 12, color: C.textMuted }}>by {course.instructor}</div>
            </div>
          </div>
          <button className="btn-ghost" onClick={onClose} style={{ fontSize: 18, padding: "4px 10px" }}>✕</button>
        </div>
        <h2 style={{ fontFamily: F.display, fontSize: 22, fontWeight: 800, marginBottom: 12 }}>{course.title}</h2>
        <div style={{ display: "flex", gap: 16, marginBottom: 20, flexWrap: "wrap" }}>
          <span style={{ fontSize: 12, color: C.textMuted }}>⏱ {course.duration}</span>
          <span style={{ fontSize: 12, color: C.textMuted }}>📦 {course.modules} modules</span>
          <span style={{ fontSize: 12, color: C.textMuted }}>📊 {course.level}</span>
          <StarRating rating={course.rating} />
          <span style={{ fontSize: 12, color: C.textMuted }}>({course.students.toLocaleString()})</span>
        </div>
        <div style={{ marginBottom: 20 }}>
  {realCourse?.mux_playback_id ? (
    <VideoPlayer playbackId={realCourse.mux_playback_id} title={course.title} />
  ) : (
    <div className="video-player" style={{ border: `1px solid ${C.border}` }}>
      {playing ? (
        <div style={{ color: C.textMuted, fontSize: 14, textAlign: "center" }}>
          <div style={{ fontSize: 36, marginBottom: 8, animation: "spin 2s linear infinite" }}>⚙️</div>
          Video player loads in production
        </div>
      ) : (
        <div style={{ textAlign: "center", cursor: "pointer" }} onClick={() => setPlaying(true)}>
          <div style={{ width: 56, height: 56, borderRadius: "50%", background: C.accent, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 10px", fontSize: 20, boxShadow: `0 0 32px ${C.accentGlow}` }}>▶</div>
          <div style={{ fontSize: 12, color: C.textMuted }}>Watch Preview (Free)</div>
        </div>
      )}
    </div>
  )}
</div>
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 10, color: C.textMuted, textTransform: "uppercase", letterSpacing: "0.5px" }}>Course Modules</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 200, overflowY: "auto" }}>
            {modules.map(m => (
              <div key={m.id}
                onClick={() => !m.done && updateProgress(m.id)}
                style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "9px 14px", borderRadius: 9, background: m.done ? C.accentGlow2 : C.surface, border: `1px solid ${m.done ? C.accent + "30" : C.border}`, cursor: m.done ? "default" : "pointer", transition: "all 0.2s" }}
                onMouseEnter={e => { if (!m.done) e.currentTarget.style.borderColor = C.accent; }}
                onMouseLeave={e => { if (!m.done) e.currentTarget.style.borderColor = C.border; }}
              >
                <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                  <span style={{ fontSize: 13 }}>{m.done ? "✅" : "🔒"}</span>
                  <span style={{ fontSize: 12, color: m.done ? C.text : C.textMuted }}>{m.title}</span>
                </div>
                <span style={{ fontSize: 11, color: C.textFaint }}>{m.done ? m.duration : "Click to complete"}</span>
              </div>
            ))}
          </div>
        </div>
        <div style={{ display: "flex", gap: 12 }}>
          {enrolled ? (
            <button className="btn-primary" style={{ flex: 1, padding: "14px" }}>▶ Continue Learning</button>
          ) : (
            <>
              <button className="btn-primary" style={{ flex: 1, padding: "14px" }} onClick={async () => {
  setEnrolled(true);
  if (user?.id) {
    await supabase.from("enrollments").insert({
      user_id: user.id,
      course_id: course.id,
      course_title: course.title,
      course_category: course.category,
      progress: 0,
    }).then(() => console.log("Enrolled in:", course.title));
  }
}}>
                {course.price === 0 ? "🚀 Enroll Free" : `💳 Enroll — $${course.price}`}
              </button>
              <button className="btn-secondary">♡ Save</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function AIPathModal({ onClose, user, setPage }) {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [loadingMsg, setLoadingMsg] = useState("Analyzing your goals...");
  const msgs = ["Analyzing your goals...","Scanning 12 courses...","Mapping your progression...","Building your path..."];
  const handleAnswer = (ans) => {
    const newAns = [...answers, ans];
    setAnswers(newAns);
    if (step < skillQuestions.length - 1) { setStep(step + 1); }
    else {
      setGenerating(true);
      let i = 0;
      const t = setInterval(() => { i = (i + 1) % msgs.length; setLoadingMsg(msgs[i]); }, 1200);
      generateSkillPath(newAns).then(r => { setResult({ ...r, answers: newAns }); }).catch(() => setError("Something went wrong. Please try again.")).finally(() => { clearInterval(t); setGenerating(false); });
    }
  };
  const reset = () => { setStep(0); setAnswers([]); setResult(null); setError(null); setGenerating(false); };
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
        {!result && !generating && !error && (
          <>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: C.accent, letterSpacing: "1.5px", textTransform: "uppercase", marginBottom: 4 }}>AI Skill Path</div>
                <div style={{ fontSize: 12, color: C.textMuted }}>Question {step + 1} of {skillQuestions.length}</div>
              </div>
              <button className="btn-ghost" onClick={onClose}>✕</button>
            </div>
            <div className="progress-bar" style={{ marginBottom: 24 }}>
              <div className="progress-fill" style={{ width: `${(step / skillQuestions.length) * 100}%` }} />
            </div>
            <h2 style={{ fontFamily: F.display, fontSize: 21, fontWeight: 800, marginBottom: 20 }}>{skillQuestions[step].q}</h2>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {skillQuestions[step].opts.map(opt => (
                <button key={opt} onClick={() => handleAnswer(opt)}
                  style={{ padding: "14px 18px", borderRadius: 12, background: C.surface, border: `1px solid ${C.border}`, color: C.text, font: "inherit", fontSize: 14, cursor: "pointer", textAlign: "left", transition: "all 0.2s", display: "flex", alignItems: "center", justifyContent: "space-between" }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = C.accent; e.currentTarget.style.background = C.accentGlow; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.background = C.surface; }}>
                  {opt} <span style={{ color: C.textFaint }}>→</span>
                </button>
              ))}
            </div>
          </>
        )}
        {generating && (
          <div style={{ textAlign: "center", padding: "40px 20px" }}>
            <LogoIcon size={56} />
            <div style={{ fontFamily: F.display, fontSize: 20, fontWeight: 800, marginTop: 16, marginBottom: 8 }}>Claude AI is thinking...</div>
            <div style={{ color: C.textMuted, fontSize: 13, marginBottom: 24 }}>{loadingMsg}</div>
            <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
              {[0,1,2].map(i => <div key={i} style={{ width: 8, height: 8, borderRadius: "50%", background: C.accent, animation: `pulse 1.2s ease ${i*0.2}s infinite` }} />)}
            </div>
          </div>
        )}
        {error && (
          <div style={{ textAlign: "center", padding: "40px 20px" }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>⚠️</div>
            <div style={{ fontFamily: F.display, fontSize: 20, fontWeight: 800, marginBottom: 12 }}>Something went wrong</div>
            <div style={{ color: C.textMuted, fontSize: 13, marginBottom: 20 }}>{error}</div>
            <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
              <button className="btn-primary" onClick={reset}>Try Again</button>
              <button className="btn-ghost" onClick={onClose}>Close</button>
            </div>
          </div>
        )}
        {result && (
          <div className="fade-in">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <span style={{ fontSize: 10, fontWeight: 700, color: C.green, letterSpacing: "1px", textTransform: "uppercase" }}>✅ Your Path is Ready</span>
              <button className="btn-ghost" onClick={onClose}>✕</button>
            </div>
            <h2 style={{ fontFamily: F.display, fontSize: 20, fontWeight: 800, marginBottom: 8 }}>Your Personalized Path</h2>
            <div style={{ padding: "12px 16px", borderRadius: 10, background: C.accentGlow, border: `1px solid ${C.accent}30`, marginBottom: 18 }}>
              <div style={{ fontSize: 13, color: C.accent, lineHeight: 1.6 }}>🎯 {result.summary}</div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 18 }}>
              {result.path.map((course, i) => (
                <div key={i} style={{ display: "flex", gap: 12, alignItems: "flex-start", padding: "13px 14px", borderRadius: 10, background: C.surface, border: `1px solid ${C.border}` }}>
                  <div style={{ width: 28, height: 28, borderRadius: "50%", background: C.accent, color: "#020617", fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, flexShrink: 0 }}>{i+1}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 3 }}>{course.thumbnail} {course.title}</div>
                    <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 4 }}>{course.duration} · {course.level}</div>
                    <div style={{ fontSize: 11, color: C.accent, fontStyle: "italic", lineHeight: 1.5 }}>{course.reason}</div>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", borderRadius: 9, background: C.surface, border: `1px solid ${C.border}`, marginBottom: 16 }}>
              <span style={{ fontSize: 12, color: C.textMuted }}>⏱ {result.timeEstimate}</span>
              <button className="btn-ghost" style={{ fontSize: 11 }} onClick={reset}>↺ Regenerate</button>
            </div>
            <button className="btn-primary" style={{ width: "100%", padding: "14px" }} onClick={async () => {
  console.log("[StartPath] user?.id:", user?.id);
  console.log("[StartPath] result?.path:", result?.path);
  if (user?.id && result?.path) {
    await Promise.all(result.path.map(async (course) => {
      const payload = {
        user_id: user.id,
        course_id: course.id,
        course_title: course.title,
        course_category: course.category,
        progress: 0,
      };
      console.log("[StartPath] Inserting enrollment:", payload);
      const { data, error } = await supabase.from("enrollments").insert(payload);
      console.log("[StartPath] Insert result — data:", data, "error:", error);
    }));
  } else {
    console.warn("[StartPath] Skipped insert — user?.id:", user?.id, "result?.path:", result?.path);
  }
  onClose();
  if (setPage) setPage("learning");
}}>
  🚀 Start This Path
</button>
          </div>
        )}
      </div>
    </div>
  );
}

function HomePage({ setPage, setSelectedCourse, user }) {
  const [showAIModal, setShowAIModal] = useState(false);
  const { openSignIn } = useClerk();

  const D = {
    bg:       '#020617',
    surface:  'rgba(255,255,255,0.05)',
    border:   'rgba(255,255,255,0.10)',
    accent:   '#60A5FA',
    purple:   '#A78BFA',
    text:     '#FFFFFF',
    muted:    '#94A3B8',
  };

  const sectionBase = { maxWidth: 1200, margin: '0 auto', padding: '0 24px' };

  return (
    <div className="fade-in" style={{ background: D.bg, color: D.text, fontFamily: F.body }}>
      {showAIModal && <AIPathModal onClose={() => setShowAIModal(false)} user={user} setPage={setPage} />}

      {/* ── HERO ── */}
      <div style={{ position: 'relative', overflow: 'hidden', padding: '100px 24px 80px' }}>
        {/* Glow blobs */}
        <div style={{ position: 'absolute', top: -100, left: '20%', width: 500, height: 500, background: 'radial-gradient(circle, rgba(96,165,250,0.15) 0%, transparent 65%)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', top: 0, right: '10%', width: 400, height: 400, background: 'radial-gradient(circle, rgba(167,139,250,0.12) 0%, transparent 65%)', pointerEvents: 'none' }} />

        <div style={{ ...sectionBase, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 64, alignItems: 'center' }}>
          {/* Left */}
          <div>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 14px', borderRadius: 50, background: 'rgba(96,165,250,0.12)', border: '1px solid rgba(96,165,250,0.3)', marginBottom: 24, fontSize: 12, color: D.accent, fontWeight: 700 }}>
              🚀 2026's #1 Micro-Skills Platform
            </div>

            <h1 style={{ fontFamily: F.display, fontSize: 'clamp(36px,4.5vw,62px)', fontWeight: 900, lineHeight: 1.1, marginBottom: 20, color: D.text }}>
              Learn the Skills<br />
              That <span style={{ background: 'linear-gradient(135deg, #60A5FA, #A78BFA)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Pay</span> in 2026
            </h1>

            <p style={{ fontSize: 17, color: D.muted, lineHeight: 1.75, marginBottom: 36, maxWidth: 480 }}>
              AI-personalized micro-courses in 5–15 minute modules. From complete beginner to income-generating in 30 days.
            </p>

            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 52 }}>
              <button
                onClick={() => user ? setShowAIModal(true) : openSignIn()}
                style={{ padding: '14px 28px', borderRadius: 10, background: '#FFFFFF', color: '#020617', border: 'none', fontWeight: 700, fontSize: 15, cursor: 'pointer', transition: 'all 0.2s', fontFamily: F.body }}
                onMouseEnter={e => { e.currentTarget.style.background = '#F1F5F9'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = '#FFFFFF'; e.currentTarget.style.transform = 'none'; }}
              >🧠 Build My Skill Path</button>
              <button
                onClick={() => setPage('courses')}
                style={{ padding: '14px 28px', borderRadius: 10, background: 'transparent', color: D.text, border: '1.5px solid rgba(255,255,255,0.25)', fontWeight: 600, fontSize: 15, cursor: 'pointer', transition: 'all 0.2s', fontFamily: F.body }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.5)'; e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.25)'; e.currentTarget.style.background = 'transparent'; }}
              >Browse Courses</button>
            </div>

            {/* Stats */}
            <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap' }}>
              {[
                { n: '47K+', l: 'Learners' },
                { n: '98%', l: 'Complete Rate' },
                { n: '$4.2M', l: 'Paid to Creators' },
              ].map(s => (
                <div key={s.l}>
                  <div style={{ fontFamily: F.display, fontSize: 28, fontWeight: 900, color: D.accent }}>{s.n}</div>
                  <div style={{ fontSize: 12, color: D.muted, marginTop: 2 }}>{s.l}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right — mock dashboard card */}
          <div style={{ position: 'relative' }}>
            <div style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 20, padding: 24, backdropFilter: 'blur(20px)' }}>
              {/* Card header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: D.text }}>My Learning Dashboard</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 50, background: 'rgba(240,165,0,0.15)', border: '1px solid rgba(240,165,0,0.3)' }}>
                  <span style={{ fontSize: 12 }}>🔥</span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#F0A500' }}>7 day streak</span>
                </div>
              </div>

              {/* Current course */}
              <div style={{ background: 'rgba(96,165,250,0.08)', border: '1px solid rgba(96,165,250,0.2)', borderRadius: 14, padding: 16, marginBottom: 16 }}>
                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 12 }}>
                  <div style={{ fontSize: 28, flexShrink: 0 }}>🤖</div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: D.text, marginBottom: 3 }}>Master ChatGPT Agents in 1 Hour</div>
                    <div style={{ fontSize: 11, color: D.muted }}>Module 4 of 8 · AI Workflows</div>
                  </div>
                </div>
                <div style={{ height: 6, borderRadius: 3, background: 'rgba(255,255,255,0.1)', overflow: 'hidden' }}>
                  <div style={{ width: '47%', height: '100%', background: 'linear-gradient(90deg,#60A5FA,#A78BFA)', borderRadius: 3 }} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
                  <span style={{ fontSize: 11, color: D.muted }}>47% complete</span>
                  <span style={{ fontSize: 11, color: D.accent }}>Continue →</span>
                </div>
              </div>

              {/* AI recommendation */}
              <div style={{ background: 'rgba(167,139,250,0.08)', border: '1px solid rgba(167,139,250,0.2)', borderRadius: 12, padding: 14, marginBottom: 14 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: D.purple, letterSpacing: '0.8px', textTransform: 'uppercase', marginBottom: 6 }}>✨ AI Recommends Next</div>
                <div style={{ fontSize: 12, fontWeight: 600, color: D.text }}>💰 Earn $5K/Month Selling AI Templates</div>
                <div style={{ fontSize: 11, color: D.muted, marginTop: 2 }}>Matches your Solopreneur goal</div>
              </div>

              {/* Mini stats */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                {[
                  { n: '12', l: 'Lessons done' },
                  { n: '4.2h', l: 'Total time' },
                  { n: '2', l: 'Certs earned' },
                ].map(s => (
                  <div key={s.l} style={{ background: 'rgba(255,255,255,0.04)', borderRadius: 10, padding: '10px 8px', textAlign: 'center' }}>
                    <div style={{ fontSize: 16, fontWeight: 800, color: D.text }}>{s.n}</div>
                    <div style={{ fontSize: 9, color: D.muted, marginTop: 2 }}>{s.l}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Floating badge */}
            <div style={{ position: 'absolute', top: -16, right: -16, background: 'rgba(10,138,78,0.9)', border: '1px solid rgba(10,138,78,0.5)', borderRadius: 10, padding: '8px 14px', fontSize: 11, fontWeight: 700, color: '#FFFFFF', animation: 'float 3s ease-in-out infinite' }}>
              ✅ 12,400 learning today
            </div>
          </div>
        </div>
      </div>

      {/* ── TRUST BAR ── */}
      <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', borderBottom: '1px solid rgba(255,255,255,0.08)', padding: '20px 24px' }}>
        <div style={{ ...sectionBase, display: 'flex', gap: 0, justifyContent: 'center', flexWrap: 'wrap' }}>
          {[
            { icon: '🧠', text: 'Personalized learning' },
            { icon: '⚡', text: 'Fast daily sessions' },
            { icon: '📊', text: 'Progress visibility' },
            { icon: '💰', text: 'Practical skill outcomes' },
          ].map((item, i, arr) => (
            <div key={item.text} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 32px', borderRight: i < arr.length - 1 ? '1px solid rgba(255,255,255,0.1)' : 'none' }}>
              <span style={{ fontSize: 16 }}>{item.icon}</span>
              <span style={{ fontSize: 13, color: D.muted, fontWeight: 500 }}>{item.text}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── HOW IT WORKS ── */}
      <div style={{ padding: '80px 24px' }}>
        <div style={sectionBase}>
          <div style={{ textAlign: 'center', marginBottom: 56 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: D.accent, letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: 12 }}>Simple Process</div>
            <h2 style={{ fontFamily: F.display, fontSize: 'clamp(26px,3vw,40px)', fontWeight: 900, color: D.text }}>How SkillBite Works</h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 32 }}>
            {[
              { step: '01', icon: '🎯', title: 'Choose your goal', desc: 'Tell us what you want — a new income stream, a career change, or a specific skill. Takes 60 seconds.' },
              { step: '02', icon: '🧠', title: 'Get your AI roadmap', desc: 'Our AI builds a personalized path of 3–5 courses in the exact order you need them. No guesswork.' },
              { step: '03', icon: '⚡', title: 'Learn in minutes a day', desc: 'Complete 5–15 minute modules at your own pace. Most learners reach their goal in under 30 days.' },
            ].map(item => (
              <div key={item.step} style={{ position: 'relative', padding: 28, borderRadius: 18, background: D.surface, border: `1px solid ${D.border}` }}>
                <div style={{ position: 'absolute', top: 20, right: 20, fontFamily: F.display, fontSize: 40, fontWeight: 900, color: 'rgba(96,165,250,0.12)', lineHeight: 1 }}>{item.step}</div>
                <div style={{ fontSize: 36, marginBottom: 16 }}>{item.icon}</div>
                <h3 style={{ fontSize: 17, fontWeight: 700, color: D.text, marginBottom: 10 }}>{item.title}</h3>
                <p style={{ fontSize: 13, color: D.muted, lineHeight: 1.7 }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── FEATURES GRID ── */}
      <div style={{ padding: '0 24px 80px' }}>
        <div style={sectionBase}>
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: D.purple, letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: 12 }}>Why SkillBite</div>
            <h2 style={{ fontFamily: F.display, fontSize: 'clamp(26px,3vw,40px)', fontWeight: 900, color: D.text }}>Built Different</h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 20 }}>
            {[
              { icon: '🧠', title: 'AI-personalized paths', desc: 'Claude AI analyzes your goals, pace, and skill level to build a learning path uniquely yours — not a generic curriculum.' },
              { icon: '⏱', title: '5–15 min daily lessons', desc: 'Every module is engineered for a single sitting. Learn on your commute, lunch break, or between meetings.' },
              { icon: '💰', title: 'Skill-to-income focus', desc: 'Every course is designed around a measurable outcome: a marketable skill, a side income, or a new job-ready capability.' },
              { icon: '📊', title: 'Track real progress', desc: 'Visual progress bars, streak tracking, and AI check-ins keep you accountable and motivated every step of the way.' },
            ].map(f => (
              <div key={f.title} style={{ padding: 28, borderRadius: 18, background: D.surface, border: `1px solid ${D.border}`, display: 'flex', gap: 20, alignItems: 'flex-start' }}>
                <div style={{ fontSize: 32, flexShrink: 0 }}>{f.icon}</div>
                <div>
                  <h3 style={{ fontSize: 16, fontWeight: 700, color: D.text, marginBottom: 8 }}>{f.title}</h3>
                  <p style={{ fontSize: 13, color: D.muted, lineHeight: 1.7 }}>{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── FOR WHO ── */}
      <div style={{ padding: '0 24px 80px' }}>
        <div style={sectionBase}>
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: D.accent, letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: 12 }}>Who It's For</div>
            <h2 style={{ fontFamily: F.display, fontSize: 'clamp(26px,3vw,40px)', fontWeight: 900, color: D.text }}>Made For You</h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 20 }}>
            {[
              { icon: '👤', title: 'For Individuals', color: D.accent, points: ['Build a side income with AI skills', 'Learn at your own pace daily', 'Personalized path from day one', 'Certificates to show employers'] },
              { icon: '🏢', title: 'For Teams', color: D.purple, points: ['Upskill your whole team in AI', 'Track team progress centrally', 'Custom learning tracks by role', 'Volume pricing from 5+ seats'] },
              { icon: '🎓', title: 'For Educators', color: '#34D399', points: ['List your courses on SkillBite', 'Keep 65–80% of every sale', 'Built-in audience of 47K+', 'Full analytics dashboard'] },
            ].map(card => (
              <div key={card.title} style={{ padding: 28, borderRadius: 18, background: D.surface, border: `1px solid ${D.border}` }}>
                <div style={{ fontSize: 32, marginBottom: 14 }}>{card.icon}</div>
                <h3 style={{ fontSize: 18, fontWeight: 700, color: D.text, marginBottom: 16 }}>{card.title}</h3>
                <ul style={{ listStyle: 'none', padding: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {card.points.map(p => (
                    <li key={p} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 13, color: D.muted, lineHeight: 1.5 }}>
                      <span style={{ color: card.color, marginTop: 1, flexShrink: 0 }}>✓</span>
                      {p}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── FINAL CTA ── */}
      <div style={{ padding: '0 24px 100px' }}>
        <div style={{ ...sectionBase, background: 'linear-gradient(135deg, rgba(96,165,250,0.15), rgba(167,139,250,0.10))', border: '1px solid rgba(96,165,250,0.2)', borderRadius: 24, padding: '72px 48px', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: -60, left: '30%', width: 300, height: 300, background: 'radial-gradient(circle, rgba(96,165,250,0.12) 0%, transparent 70%)', pointerEvents: 'none' }} />
          <div style={{ fontSize: 11, fontWeight: 700, color: D.accent, letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: 16 }}>Start Today — Free</div>
          <h2 style={{ fontFamily: F.display, fontSize: 'clamp(28px,3.5vw,48px)', fontWeight: 900, color: D.text, marginBottom: 16 }}>
            Your first skill is one click away
          </h2>
          <p style={{ fontSize: 16, color: D.muted, maxWidth: 480, margin: '0 auto 36px', lineHeight: 1.75 }}>
            No credit card required. Start with any free course and let the AI build your personalized roadmap.
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={() => user ? setShowAIModal(true) : openSignIn()}
              style={{ padding: '15px 36px', borderRadius: 10, background: '#FFFFFF', color: '#020617', border: 'none', fontWeight: 700, fontSize: 15, cursor: 'pointer', transition: 'all 0.2s', fontFamily: F.body }}
              onMouseEnter={e => { e.currentTarget.style.background = '#F1F5F9'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = '#FFFFFF'; e.currentTarget.style.transform = 'none'; }}
            >🚀 Start Free</button>
            <button
              onClick={() => setPage('courses')}
              style={{ padding: '15px 36px', borderRadius: 10, background: 'transparent', color: D.text, border: '1.5px solid rgba(255,255,255,0.25)', fontWeight: 600, fontSize: 15, cursor: 'pointer', transition: 'all 0.2s', fontFamily: F.body }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.5)'; e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.25)'; e.currentTarget.style.background = 'transparent'; }}
            >Browse Courses</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function CoursesPage({ setSelectedCourse }) {
  const [category, setCategory] = useState("All");
  const [region, setRegion] = useState("All");
  const [sort, setSort] = useState("Popular");
  const [search, setSearch] = useState("");
  const filtered = courses
    .filter(c => category === "All" || c.category === category)
    .filter(c => region === "All" || c.region === region)
    .filter(c => c.title.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => sort === "Popular" ? b.students - a.students : sort === "Newest" ? (b.new ? 1 : -1) - (a.new ? 1 : -1) : b.rating - a.rating);
  return (
    <div className="fade-in page-container" style={{ maxWidth: 1200, margin: "0 auto", padding: "32px 24px 80px" }}>
      <div style={{ marginBottom: 28 }}>
        <h1 className="section-title" style={{ marginBottom: 6 }}>Browse Courses</h1>
        <p style={{ color: C.textMuted, fontSize: 14 }}>{courses.length} micro-courses across all skill areas</p>
      </div>
      <div style={{ display: "flex", gap: 10, marginBottom: 18, flexWrap: "wrap", alignItems: "center" }}>
        <input className="input-field" placeholder="🔍  Search courses..." value={search} onChange={e => setSearch(e.target.value)} style={{ maxWidth: 260 }} />
        <select className="input-field" value={sort} onChange={e => setSort(e.target.value)} style={{ maxWidth: 150 }}>
          {["Popular","Newest","Top Rated"].map(s => <option key={s}>{s}</option>)}
        </select>
        <select className="input-field" value={region} onChange={e => setRegion(e.target.value)} style={{ maxWidth: 160 }}>
          <option value="All">🌍 All Regions</option>
          {regions.map(r => <option key={r} value={r}>{r === "Nigeria" ? "🇳🇬" : r === "India" ? "🇮🇳" : r === "US" ? "🇺🇸" : "🌍"} {r}</option>)}
        </select>
      </div>
      <div className="pill-selector" style={{ marginBottom: 24 }}>
        {categories.map(c => <button key={c} className={`pill ${category === c ? "active" : ""}`} onClick={() => setCategory(c)}>{c}</button>)}
      </div>
      <div className="grid-courses">
        {filtered.map(c => <CourseCard key={c.id} course={c} onClick={setSelectedCourse} />)}
      </div>
      {filtered.length === 0 && (
        <div style={{ textAlign: "center", padding: "60px 0", color: C.textMuted }}>
          <div style={{ fontSize: 36, marginBottom: 10 }}>🔍</div>
          No courses match your search.
        </div>
      )}
    </div>
  );
}

function PacksPage({ user }) {
  const packCourses = {
    1: [1, 2, 7],
    2: [3, 9],
    3: [2, 5, 7],
    4: [6, 8],
    5: [1, 4, 6, 8],
    6: [6, 5, 12],
  };
  return (
    <div className="fade-in page-container" style={{ maxWidth: 1200, margin: "0 auto", padding: "32px 24px 80px" }}>
      <div style={{ marginBottom: 28 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: C.accent, letterSpacing: "1.5px", textTransform: "uppercase", marginBottom: 8 }}>30-Day Transformation</div>
        <h1 className="section-title" style={{ marginBottom: 6 }}>Career Packs</h1>
        <p style={{ color: C.textMuted, fontSize: 14 }}>Structured 30-day tracks. Zero to income-ready.</p>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(340px,1fr))", gap: 24 }}>
        {careerPacks.map(pack => (
          <div key={pack.id} className="card" style={{ cursor: "pointer", position: "relative" }}>
            <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 4, background: `linear-gradient(90deg,${pack.color},transparent)`, borderRadius: "16px 16px 0 0" }} />
            <div style={{ background: `linear-gradient(135deg,${pack.color}18,${C.surface})`, padding: "32px 28px 24px", borderBottom: `1px solid ${C.border}` }}>
              <div style={{ fontSize: 48, marginBottom: 10 }}>{pack.icon}</div>
              <h3 style={{ fontFamily: F.display, fontSize: 21, fontWeight: 800, marginBottom: 8, lineHeight: 1.3 }}>{pack.title}</h3>
              <p style={{ fontSize: 13, color: C.textMuted, lineHeight: 1.6 }}>{pack.desc}</p>
            </div>
            <div style={{ padding: "20px 28px" }}>
              <div style={{ display: "flex", gap: 20, marginBottom: 16 }}>
                {[["DURATION", pack.duration], ["COURSES", `${pack.courses} modules`], ["STUDENTS", pack.students.toLocaleString()]].map(([l, v]) => (
                  <div key={l}><div style={{ fontSize: 9, color: C.textMuted, marginBottom: 2, letterSpacing: "0.5px" }}>{l}</div><div style={{ fontSize: 14, fontWeight: 700 }}>{v}</div></div>
                ))}
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ fontSize: 26, fontWeight: 900, color: C.gold, fontFamily: F.display }}>${pack.price}</div>
                <button className="btn-primary" onClick={async () => {
                  if (!user) { alert("Please sign in to enroll"); return; }
                  const courseIds = packCourses[pack.id] || [];
                  for (const courseId of courseIds) {
                    const course = courses.find(c => c.id === courseId);
                    if (course) {
                      await supabase.from("enrollments").insert({
                        user_id: user.id,
                        course_id: courseId,
                        course_title: course.title,
                        course_category: course.category,
                        progress: 0,
                      }).catch(() => {});
                    }
                  }
                  alert("Enrolled in all courses! Check My Learning.");
                }}>Enroll Now →</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function MarketplacePage({ user, setPage }) {
  const templates = [
    { id: 1, title: "ChatGPT Agent Prompt Pack (50 Prompts)", type: "Templates", price: 19, seller: "Alex R.", sales: 840, icon: "📦" },
    { id: 2, title: "WhatsApp Business Automation SOP", type: "Templates", price: 29, seller: "Tunde O.", sales: 420, icon: "📋" },
    { id: 3, title: "AI Landing Page Canva Template Kit", type: "Templates", price: 24, seller: "Ana C.", sales: 610, icon: "🎨" },
    { id: 4, title: "TikTok AI Video Script Templates", type: "Templates", price: 14, seller: "Maya C.", sales: 920, icon: "📝" },
    { id: 5, title: "1:1 AI Business Strategy Session", type: "Coaching", price: 99, seller: "Sarah K.", sales: 180, icon: "🎯" },
    { id: 6, title: "Solopreneur AI Stack Setup Call", type: "Coaching", price: 149, seller: "James W.", sales: 95, icon: "🚀" },
    { id: 7, title: "No-Code Automation Starter Pack", type: "Templates", price: 34, seller: "Marcus L.", sales: 390, icon: "⚡" },
    { id: 8, title: "AI Client Onboarding Email Sequence", type: "Templates", price: 22, seller: "Lisa P.", sales: 540, icon: "📧" },
  ];
  const [activeTab, setActiveTab] = useState("All");
  const [search, setSearch] = useState('');
  const [priceRange, setPriceRange] = useState('All');
  const tabs = ["All","Templates","Coaching","Courses"];
  const filtered = templates
    .filter(t => activeTab === 'All' || t.type === activeTab)
    .filter(t => search === '' || t.title.toLowerCase().includes(search.toLowerCase()) || t.seller.toLowerCase().includes(search.toLowerCase()))
    .filter(t => {
      if (priceRange === 'Under $20') return t.price < 20;
      if (priceRange === '$20–$50') return t.price >= 20 && t.price <= 50;
      if (priceRange === '$50–$100') return t.price > 50 && t.price <= 100;
      if (priceRange === '$100+') return t.price > 100;
      return true;
    });
  return (
    <div className="fade-in page-container" style={{ maxWidth: 1200, margin: "0 auto", padding: "32px 24px 80px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 28 }}>
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: C.accent, letterSpacing: "1.5px", textTransform: "uppercase", marginBottom: 8 }}>Expert-Created</div>
          <h1 className="section-title">Marketplace</h1>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <input className="input-field" placeholder="🔍 Search..." value={search} onChange={e => setSearch(e.target.value)} style={{ maxWidth: 180 }} />
          <button className="btn-primary" onClick={() => {
            if (user) { setPage('creator'); }
            else { alert('Please sign in to list a product'); }
          }}>+ List Product</button>
        </div>
      </div>
      <div className="marketplace-layout" style={{ display: "grid", gridTemplateColumns: "220px 1fr", gap: 28 }}>
        <div className="marketplace-sidebar">
          <div style={{ fontSize: 10, fontWeight: 700, color: C.textMuted, letterSpacing: "1px", textTransform: "uppercase", marginBottom: 10 }}>Category</div>
          {tabs.map(t => (
            <button key={t} className={`sidebar-item ${activeTab === t ? "active" : ""}`} onClick={() => setActiveTab(t)}>
              <span>{t === "All" ? "🏪" : t === "Templates" ? "📦" : t === "Coaching" ? "🎯" : "📚"}</span> {t}
            </button>
          ))}
          <div style={{ borderTop: `1px solid ${C.border}`, margin: "16px 0", paddingTop: 16 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: C.textMuted, letterSpacing: "1px", textTransform: "uppercase", marginBottom: 10 }}>Price Range</div>
            {['All', 'Under $20', '$20–$50', '$50–$100', '$100+'].map(r => (
              <button key={r} className={`sidebar-item ${priceRange === r ? 'active' : ''}`} onClick={() => setPriceRange(r)}>{r}</button>
            ))}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 16 }}>{filtered.length} products found</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(240px,1fr))", gap: 14 }}>
            {filtered.map(item => (
              <div key={item.id} className="card" style={{ cursor: "pointer", padding: 18 }}>
                <div style={{ fontSize: 32, marginBottom: 10 }}>{item.icon}</div>
                <Badge color={item.type === "Coaching" ? C.violet : C.accent}>{item.type}</Badge>
                <h3 style={{ fontSize: 13, fontWeight: 700, margin: "8px 0 6px", lineHeight: 1.4 }}>{item.title}</h3>
                <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 12 }}>by {item.seller} · {item.sales} sales</div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div style={{ fontSize: 18, fontWeight: 900, color: C.gold, fontFamily: F.display }}>${item.price}</div>
                  <button className="btn-primary" style={{ padding: "7px 14px", fontSize: 12 }}>Buy</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function CreatorPage() {
  const [myCourses, setMyCourses] = useState([]);
const [loadingCourses, setLoadingCourses] = useState(true);
const { user } = useUser();
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
  if (!user?.id) {
    setLoadingCourses(false);
    return;
  }
  setLoadingCourses(true);
  supabase
    .from("courses")
    .select("*")
    .eq("creator_id", user.id)
    .order("created_at", { ascending: false })
    .then(({ data, error }) => {
      console.log("Courses fetched:", data, error);
      if (data) setMyCourses(data);
      setLoadingCourses(false);
    });
}, [user?.id, activeTab]);
  
  const earnings = [
    { month: "Oct", amt: 1200 },{ month: "Nov", amt: 1800 },{ month: "Dec", amt: 2400 },
    { month: "Jan", amt: 2100 },{ month: "Feb", amt: 3200 },{ month: "Mar", amt: 3800 },
  ];
  const maxE = Math.max(...earnings.map(e => e.amt));
 // const myCourses = [
  //  { title: "Master ChatGPT Agents in 1 Hour", students: 2400, revenue: 1800, rating: 4.9, status: "Published" },
  //  { title: "Earn $5K/Month with AI Templates", students: 880, revenue: 960, rating: 4.7, status: "Published" },
  //  { title: "AI Automation for Beginners", students: 0, revenue: 0, rating: 0, status: "Draft" },
 // ];
  return (
    <div className="fade-in page-container" style={{ maxWidth: 1200, margin: "0 auto", padding: "32px 24px 80px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 28 }}>
        <div>
          <h1 className="section-title">Creator Studio</h1>
          <p style={{ color: C.textMuted, fontSize: 13, marginTop: 4 }}>Welcome back, Alex Rivera 👋</p>
        </div>
        <button className="btn-primary">+ Create Course</button>
      </div>
      <div className="creator-stats" style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 14, marginBottom: 28 }}>
        {[
          { label: "Total Students", value: "3,280", icon: "👥", delta: "+12%", color: C.accent },
          { label: "Total Revenue", value: "$14,760", icon: "💰", delta: "+18%", color: C.gold },
          { label: "Active Courses", value: "2", icon: "📚", delta: "1 draft", color: C.green },
          { label: "Avg. Rating", value: "4.85", icon: "⭐", delta: "Top 10%", color: C.violet },
        ].map(s => (
          <div key={s.label} className="stat-card">
            <div style={{ fontSize: 24, marginBottom: 6 }}>{s.icon}</div>
            <div style={{ fontSize: 22, fontWeight: 900, fontFamily: F.display, color: s.color, marginBottom: 2 }}>{s.value}</div>
            <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 4 }}>{s.label}</div>
            <div style={{ fontSize: 10, color: C.green }}>{s.delta}</div>
          </div>
        ))}
      </div>
      <div className="tab-bar">
        {["overview","courses","earnings","upload"].map(t => (
          <button key={t} className={`tab ${activeTab === t ? "active" : ""}`} onClick={() => setActiveTab(t)}>
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>
      {activeTab === "overview" && (
        <div className="creator-earnings" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
          <div className="card" style={{ padding: 24 }}>
            <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 18, color: C.textMuted, textTransform: "uppercase", letterSpacing: "0.5px" }}>Monthly Earnings</div>
            <div style={{ display: "flex", alignItems: "flex-end", gap: 6, height: 130 }}>
              {earnings.map(e => (
                <div key={e.month} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
                  <div style={{ width: "100%", background: `linear-gradient(180deg,${C.accent},${C.accentDim})`, borderRadius: "4px 4px 0 0", height: `${(e.amt/maxE)*118}px`, cursor: "pointer", transition: "opacity 0.2s" }} title={`$${e.amt}`} onMouseEnter={e => e.target.style.opacity = 0.8} onMouseLeave={e => e.target.style.opacity = 1} />
                  <div style={{ fontSize: 9, color: C.textMuted }}>{e.month}</div>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 14, fontSize: 12, color: C.textMuted }}>This month: <strong style={{ color: C.gold }}>$3,800</strong> · 80% revenue share</div>
          </div>
          <div className="card" style={{ padding: 24 }}>
            <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 18, color: C.textMuted, textTransform: "uppercase", letterSpacing: "0.5px" }}>Recent Activity</div>
            {[
              { icon: "👤", msg: "New enrollment: ChatGPT Agents", time: "2 min ago" },
              { icon: "⭐", msg: "5-star review received", time: "14 min ago" },
              { icon: "💰", msg: "Payout of $1,200 processed", time: "1 hr ago" },
              { icon: "👀", msg: "342 profile views today", time: "3 hrs ago" },
              { icon: "📝", msg: "New Q&A in Module 3", time: "5 hrs ago" },
            ].map((a, i) => (
              <div key={i} style={{ display: "flex", gap: 10, alignItems: "center", padding: "9px 0", borderBottom: i < 4 ? `1px solid ${C.border}` : "none" }}>
                <span style={{ fontSize: 16 }}>{a.icon}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, fontWeight: 500 }}>{a.msg}</div>
                  <div style={{ fontSize: 10, color: C.textFaint }}>{a.time}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {activeTab === "courses" && (
  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
    {loadingCourses ? (
      <div style={{ color: C.textMuted, fontSize: 14, padding: "20px 0" }}>Loading your courses...</div>
    ) : myCourses.length === 0 ? (
      <div style={{ textAlign: "center", padding: "40px 0", color: C.textMuted }}>
        <div style={{ fontSize: 36, marginBottom: 10 }}>📚</div>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>No courses yet</div>
        <div style={{ fontSize: 12, marginBottom: 16 }}>Upload your first course to get started</div>
        <button className="btn-primary" onClick={() => setActiveTab("upload")}>+ Upload Course</button>
      </div>
    ) : (
      myCourses.map((c) => (
        <div key={c.id} className="card" style={{ padding: "18px 22px", display: "flex", alignItems: "center", gap: 16 }}>
          {c.thumbnail && (
            <img src={c.thumbnail} alt={c.title} style={{ width: 60, height: 36, objectFit: "cover", borderRadius: 6, flexShrink: 0 }} />
          )}
          <div style={{ flex: 1 }}>
            <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 4 }}>
              <h3 style={{ fontSize: 14, fontWeight: 700 }}>{c.title}</h3>
              <Badge color={c.status === "published" ? C.green : c.status === "pending" ? C.gold : C.textMuted}>
                {c.status}
              </Badge>
            </div>
            <div style={{ display: "flex", gap: 16 }}>
              <span style={{ fontSize: 12, color: C.textMuted }}>📁 {c.category}</span>
              <span style={{ fontSize: 12, color: C.textMuted }}>💰 ${c.price || "Free"}</span>
              <span style={{ fontSize: 12, color: C.textMuted }}>👥 {c.students || 0} students</span>
              {c.duration && <span style={{ fontSize: 12, color: C.textMuted }}>⏱ {c.duration}</span>}
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn-ghost" style={{ fontSize: 12 }}>Edit</button>
            <button className="btn-secondary" style={{ fontSize: 12, padding: "7px 14px" }}>Analytics</button>
          </div>
        </div>
      ))
    )}
    <button className="btn-secondary" style={{ alignSelf: "flex-start", padding: "12px 20px", fontSize: 13 }}
      onClick={() => setActiveTab("upload")}>
      + Upload New Course
    </button>
  </div>
)}
      {activeTab === "upload" && (
  <UploadCourseForm userId={user?.id} onSuccess={() => setActiveTab("courses")} />
)}
      {activeTab === "earnings" && (
        <div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 14, marginBottom: 20 }}>
            {[
              { label: "Pending Payout", value: "$1,240", color: C.accent },
              { label: "All Time Earned", value: "$14,760", color: C.green },
              { label: "Revenue Share", value: "80%", color: C.gold }
            ].map(s => (
              <div key={s.label} className="stat-card" style={{ textAlign: "center" }}>
                <div style={{ fontSize: 24, fontWeight: 900, fontFamily: F.display, color: s.color, marginBottom: 4 }}>{s.value}</div>
                <div style={{ fontSize: 11, color: C.textMuted }}>{s.label}</div>
              </div>
            ))}
          </div>
          <button className="btn-primary">Request Payout</button>
        </div>
      )}
    </div>
  );
}

export default function App() {
  const [page, setPage] = useState("home");
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [showPricing, setShowPricing] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { user, isSignedIn } = useUser();
  const { dbUser } = useSupabaseUser() || {}; // eslint-disable-line no-unused-vars

  useEffect(() => {
    if (isSignedIn && user) {
      const key = `welcome_sent_${user.id}`;
      if (!localStorage.getItem(key)) {
        sendEmail({ to: user.primaryEmailAddress?.emailAddress, type: "welcome", name: user.firstName || "there" });
        localStorage.setItem(key, "true");
      }
    }
  }, [isSignedIn, user]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [page]);

  useEffect(() => {
    const handlePopState = (e) => {
      if (e.state?.page) setPage(e.state.page);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const navigateTo = (newPage) => {
    window.history.pushState({ page: newPage }, '', `/${newPage === 'home' ? '' : newPage}`);
    setPage(newPage);
  };

  const navItems = [
    { id: "home", label: "Home" },
    { id: "courses", label: "Courses" },
    { id: "packs", label: "Career Packs" },
    { id: "marketplace", label: "Marketplace" },
    { id: "creator", label: "Creator Studio" },
  ];

  return (
    <>
      <style>{css}</style>
      <div style={{ minHeight: "100vh", background: '#020617' }}>
        {/* Navbar */}
        <nav style={{ position: "sticky", top: 0, zIndex: 50, background: 'rgba(2,6,23,0.90)', backdropFilter: "blur(20px)", borderBottom: '1px solid rgba(255,255,255,0.10)' }}>
          <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 20px", height: 66, display: "flex", alignItems: "center", gap: 8 }}>
            {/* Logo */}
            <div style={{ cursor: "pointer", marginRight: 20, flexShrink: 0 }} onClick={() => { navigateTo("home"); setMenuOpen(false); }}>
              <LogoWordmark size="sm" />
            </div>
            {/* Desktop nav */}
            <div className="nav-links-desktop" style={{ display: "flex", gap: 2, flex: 1, minWidth: 0, overflow: "hidden" }}>
              {navItems.map(item => (
                <button
                  key={item.id}
                  className={`nav-link ${page === item.id ? "active" : ""}${item.id === "packs" || item.id === "marketplace" ? " nav-hide-md" : ""}`}
                  onClick={() => navigateTo(item.id)}
                >{item.label}</button>
              ))}
              <button className="btn-ghost" onClick={() => setShowPricing(true)} style={{ fontSize: 13 }}>Pricing</button>
            </div>
            {/* Right */}
            <div style={{ display: "flex", gap: 10, alignItems: "center", flexShrink: 0, marginLeft: "auto" }}>
              <SignedIn>
                {dbUser && dbUser.plan && dbUser.plan !== "free" && (
                  <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 12px", borderRadius: 8, background: C.accentGlow, border: `1px solid ${C.accent}30` }}>
                    <div style={{ width: 8, height: 8, borderRadius: "50%", background: C.green, animation: "pulse 2s infinite" }} />
                    <span style={{ fontSize: 12, color: C.accent, fontWeight: 600, whiteSpace: "nowrap" }}>
                      {dbUser.plan === "pro" ? "Pro Plan" : "Basic Plan"}
                    </span>
                  </div>
                )}
                <UserButton afterSignOutUrl="/" appearance={{ elements: { avatarBox: { width: 34, height: 34 } } }} />
              </SignedIn>
              <SignedOut>
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexShrink: 0 }}>
                  <SignInButton mode="modal"><button className="btn-secondary" style={{ padding: "8px 18px", fontSize: 13, whiteSpace: "nowrap" }}>Sign In</button></SignInButton>
                </div>
              </SignedOut>
              {/* Hamburger */}
              <button className="mobile-menu-btn" onClick={() => setMenuOpen(!menuOpen)} style={{ display: "none", flexDirection: "column", gap: 5, background: "none", border: "none", cursor: "pointer", padding: 8 }}>
                <div style={{ width: 22, height: 2, background: menuOpen ? C.accent : C.text, borderRadius: 2, transition: "all 0.3s", transform: menuOpen ? "rotate(45deg) translate(5px,5px)" : "none" }} />
                <div style={{ width: 22, height: 2, background: C.text, borderRadius: 2, opacity: menuOpen ? 0 : 1, transition: "opacity 0.3s" }} />
                <div style={{ width: 22, height: 2, background: menuOpen ? C.accent : C.text, borderRadius: 2, transition: "all 0.3s", transform: menuOpen ? "rotate(-45deg) translate(5px,-5px)" : "none" }} />
              </button>
            </div>
          </div>
          {/* Mobile menu */}
          <div className="mobile-menu" style={{ display: "none", flexDirection: "column", background: C.surface, borderTop: `1px solid ${C.border}`, maxHeight: menuOpen ? 400 : 0, overflow: "hidden", transition: "all 0.3s ease" }}>
            {menuOpen && (
              <>
                {navItems.map(item => (
                  <button key={item.id} onClick={() => { navigateTo(item.id); setMenuOpen(false); }}
                    style={{ padding: "14px 20px", background: "none", border: "none", borderBottom: `1px solid ${C.border}`, color: page === item.id ? C.accent : C.text, fontFamily: F.body, fontSize: 15, fontWeight: page === item.id ? 700 : 500, cursor: "pointer", textAlign: "left" }}>
                    {item.label}
                  </button>
                ))}
                <button onClick={() => { setShowPricing(true); setMenuOpen(false); }}
                  style={{ padding: "14px 20px", background: "none", border: "none", borderBottom: `1px solid ${C.border}`, color: C.text, fontFamily: F.body, fontSize: 15, cursor: "pointer", textAlign: "left" }}>
                  Pricing
                </button>
                <div style={{ padding: "16px 20px" }}>
                  <SignedOut>
                    <SignInButton mode="modal"><button className="btn-primary" style={{ width: "100%", padding: "12px" }}>Get Started</button></SignInButton>
                  </SignedOut>
                </div>
              </>
            )}
          </div>
        </nav>

        {/* Pages */}
        {page === "home"        && <HomePage setPage={navigateTo} setSelectedCourse={setSelectedCourse} user={user} />}
        {page === "courses"     && <CoursesPage setSelectedCourse={setSelectedCourse} />}
        {page === "packs"       && <PacksPage user={user} />}
        {page === "marketplace" && <MarketplacePage user={user} setPage={navigateTo} />}
        {page === "creator"     && (
          user ? <CreatorPage /> :
          <div style={{ textAlign: 'center', padding: '80px 24px', color: '#FFFFFF' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>🔒</div>
            <h2 style={{ fontFamily: F.display, fontSize: 28, fontWeight: 800, marginBottom: 12 }}>Sign in to access Creator Studio</h2>
            <p style={{ color: '#94A3B8', marginBottom: 28, fontSize: 15 }}>Create and sell your own courses to 47K+ learners.</p>
            <SignInButton mode="modal"><button className="btn-primary" style={{ padding: '14px 32px', fontSize: 15 }}>Sign In</button></SignInButton>
          </div>
        )}
        {page === "terms"       && <TermsPage />}
        {page === "privacy"     && <PrivacyPage />}
        {page === "faq"         && <FAQPage setPage={navigateTo} />}
        {page === "community"   && <CommunityPage />}
        {page === "admin"       && <AdminPage user={user} />}
        {page === "learning"    && <MyLearningPage setPage={navigateTo} setSelectedCourse={setSelectedCourse} />}
        {selectedCourse && <CourseModal course={selectedCourse} onClose={() => setSelectedCourse(null)} />}
        {showPricing    && <Pricing onClose={() => setShowPricing(false)} />}

        {/* Footer */}
        <footer style={{ borderTop: 'rgba(255,255,255,0.10)', background: '#0A0F1E', padding: "36px 24px", borderTopWidth: 1, borderTopStyle: 'solid', borderTopColor: 'rgba(255,255,255,0.10)' }}>
          <div className="footer-inner" style={{ maxWidth: 1200, margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 20 }}>
            <LogoWordmark size="sm" />
            <div style={{ display: "flex", gap: 20 }}>
              {[
  { label: "Privacy", action: () => navigateTo("privacy") },
  { label: "Terms", action: () => navigateTo("terms") },
  { label: "Creators", action: () => navigateTo("creator") },
  { label: "Careers", action: null },
  { label: "Help", action: null },
  { label: "FAQ", action: () => navigateTo("faq") },
  { label: "Admin", action: () => navigateTo("admin") },
  { label: "Community", action: () => navigateTo("community") },
].map(l => (
  <span key={l.label}
    onClick={l.action || undefined}
    style={{ fontSize: 12, color: C.textMuted, cursor: l.action ? "pointer" : "default", transition: "color 0.2s" }}
    onMouseEnter={e => { if (l.action) e.target.style.color = C.accent; }}
    onMouseLeave={e => e.target.style.color = C.textMuted}>
    {l.label}
  </span>
))}
            </div>
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              {["🇳🇬","🇮🇳","🇺🇸","🇲🇽","🌍"].map(f => (
                <span key={f} style={{ fontSize: 18, cursor: "pointer", opacity: 0.6, transition: "opacity 0.2s" }}
                  onMouseEnter={e => e.target.style.opacity = 1}
                  onMouseLeave={e => e.target.style.opacity = 0.6}>{f}</span>
              ))}
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
